import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2, Zap, ShieldCheck, Wallet, Star, ArrowRight, ChevronDown,
  Home as HomeIcon, Store, Briefcase, Search, MapPin, Sparkles, MessageCircle, Droplets,
} from "lucide-react";
import ServicePrice from "../components/ServicePrice";
import { useCatalog } from "../contexts/CatalogContext";
import TestimonialsSection from "../components/home/TestimonialsSection";
import CoverageSection from "../components/home/CoverageSection";
import FaqSection from "../components/home/FaqSection";
import { servicePageForCategory, hasCatalogServices } from "../seo/pages";

const BADGE_ICONS = { "check-circle": CheckCircle2, zap: Zap, "shield-check": ShieldCheck, wallet: Wallet, star: Star };

const SegIcon = ({ id, className }) => {
  if (id === "home") return <HomeIcon className={className} />;
  if (id === "shop") return <Store className={className} />;
  return <Briefcase className={className} />;
};

export default function Home() {
  const navigate = useNavigate();
  const [city, setCity] = useState("lucknow");
  const { categories, CITIES, SEGMENTS, TRUST_BADGES } = useCatalog();
  const orderedCategories = useMemo(() => {
    const roCategory = categories.find((c) => c.id === "ro");
    if (!roCategory) return categories;
    return [roCategory, ...categories.filter((c) => c.id !== "ro")];
  }, [categories]);

  const ALL_BRANDS = Array.from(new Set(categories.flatMap((c) => c.brands)));
  const ALL_BRANDS_DOUBLED = [...ALL_BRANDS, ...ALL_BRANDS].map((name, idx) => ({ id: `${name}-${idx}`, name }));
  const FEATURED_SERVICES = categories.flatMap((c) => c.services.slice(0, 3).map((s) => ({ ...s, cat: c }))).slice(0, 12);

  return (
    <>
      {/* HERO */}
      <section className="ro-hero relative overflow-hidden bg-slate-950 text-white" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#062B46] via-[#075985] to-[#0E7490]" />
        <div className="ro-water-rings" aria-hidden="true"><span /><span /><span /></div>
        <div className="absolute -left-16 top-10 w-72 h-72 rounded-full bg-[#22C55E]/20 blur-3xl" />
        <div className="absolute right-0 top-24 w-96 h-96 rounded-full bg-[#0E7490]/20 blur-3xl" />
        <div className="absolute left-1/2 bottom-0 w-80 h-80 -translate-x-1/2 rounded-full bg-[#0369A1]/10 blur-3xl" />
        <div className="absolute inset-0 grain mix-blend-screen opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 text-white">
              <div className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
                <Droplets className="h-4 w-4" aria-hidden="true" /> Your water. Our care.
              </div>
              <a
                href="https://bot.repairro.nexitytec.cloud"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-ai-checkup-btn"
                aria-describedby="ai-checkup-description"
                className="ai-checkup-button group relative inline-flex max-w-full items-center gap-3 overflow-hidden rounded-2xl border border-emerald-200/60 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-300 p-3 pr-4 text-slate-950 sm:gap-4 sm:pr-5"
              >
                <span aria-hidden="true" className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950/10 ring-1 ring-slate-950/10">
                  <MessageCircle className="h-6 w-6 motion-safe:transition-transform motion-safe:group-hover:-rotate-12 motion-safe:group-focus-visible:-rotate-12" />
                  <Sparkles className="absolute -right-1 -top-1 h-4 w-4 rounded-full bg-white text-teal-800" />
                </span>
                <span className="relative text-left">
                  <span className="block text-sm font-extrabold sm:text-base">Free AI RO Checkup</span>
                  <span className="mt-0.5 block text-xs font-medium text-slate-800">Start a quick chat about your RO</span>
                </span>
                <span aria-hidden="true" className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/50 transition-colors group-hover:bg-white group-focus-visible:bg-white">
                  <ArrowRight className="h-4 w-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5 motion-safe:group-focus-visible:translate-x-0.5" />
                </span>
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <p id="ai-checkup-description" className="mt-3 max-w-xl text-sm text-slate-200/90">
                Tell us what’s wrong. Explore possible causes & get help booking a service.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold text-white border border-white/20 shadow-lg shadow-slate-950/20">
                <Sparkles className="w-3.5 h-3.5 text-[#A5F3FC]" /> Trusted by 500+ homes & offices
              </span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-5 leading-tight tracking-tight">
  Repairro – RO, AC & Home Appliance Services{" "}
  <br />
  <span className="text-cyan-200">at Your Doorstep in Lucknow</span>
</h1>
             <p className="mt-5 text-lg text-slate-200 max-w-2xl">
  Repairro provides trusted doorstep repair, installation, maintenance and AMC
  services for RO, AC, washing machines, fans and other home appliances in
  Lucknow, with transparent pricing and verified technicians.
</p>

              {/* City selector + Book Now */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    aria-label="Service city"
                    data-testid="hero-city-select"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full pl-12 pr-10 py-4 rounded-xl bg-white text-slate-900 font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-[#0369A1]"
                  >
                    {CITIES.map((c) => (
                      <option key={c.id} value={c.id} disabled={c.status === "soon"}>
                        {c.name} {c.status === "soon" ? "(Coming soon)" : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="hero-book-now-btn"
                    onClick={() => navigate("/booking")}
                    className="bg-[#0369A1] hover:bg-[#075985] text-white font-semibold px-7 py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    Book Now <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Inline trust row */}
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-200">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#22C55E]" /> Verified Technicians</span>
                <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-[#22C55E]" /> No Hidden Charges</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-[#0E7490] fill-[#0E7490]" /> 4.8/5 Rating</span>
              </div>
            </div>

            {/* Quick-pick service card */}
            <div className="lg:col-span-5">
              <div className="ro-quick-book bg-white rounded-2xl p-6 sm:p-7 card-shadow">
                <div className="mb-6 flex items-center gap-4 border-b border-sky-100 pb-5">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><Droplets className="h-8 w-8" aria-hidden="true" /></div>
                  <div><p className="font-display text-lg font-bold text-slate-900">Complete RO care</p><p className="mt-1 text-sm text-slate-500">Repair · Install · Maintain</p></div>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-[#0C4A6E] uppercase tracking-wider">
                  <Search className="w-4 h-4" /> Quick Book
                </div>
                <h2 className="font-display font-bold text-xl text-slate-900 mt-2">What service do you need?</h2>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  {orderedCategories.map((c) => {
                    const Icon = c.icon;
                    if (c.status === "soon") {
                      return (
                        <div key={c.id} data-testid={`hero-quick-${c.id}`} className="border border-dashed border-slate-200 rounded-xl p-4 opacity-70">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: c.bg, color: c.color }}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="font-semibold text-slate-900 text-sm">{c.short}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Coming soon</div>
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={c.id}
                        to={`/booking?category=${c.id}`}
                        data-testid={`hero-quick-${c.id}`}
                        className="group border border-slate-200 hover:border-[#0C4A6E] hover:shadow-md rounded-xl p-4 transition-all"
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                          style={{ backgroundColor: c.bg, color: c.color }}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="font-semibold text-slate-900 text-sm">{c.short}</div>
                        <div className="text-xs text-slate-500 mt-0.5">From <ServicePrice service={{ ...c, price: c.startingPrice }} /></div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="category-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">RO, AC &amp; Home Appliance Services</span>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">
  Expert repair & home services in Lucknow
</h2>
          </div>
          <Link to="/services" data-testid="see-all-services-link" className="text-sm font-semibold text-[#0369A1] hover:text-[#0369A1] flex items-center gap-1">
            See all services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((c) => {
            const Icon = c.icon;
            const disabled = c.status === "soon";
            return (
              <div
                key={c.id}
                data-testid={`category-card-${c.id}`}
                className={`bg-white rounded-2xl p-6 border border-slate-200 transition-all card-shadow ${disabled ? "opacity-70" : "hover:border-[#0369A1] hover:-translate-y-1"}`}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: c.bg, color: c.color }}
                >
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{c.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{c.tagline}</p>
                {hasCatalogServices(c) && servicePageForCategory(c.id) && (
                  <Link to={servicePageForCategory(c.id).path} className="mt-3 block text-sm font-semibold text-[#0369A1] underline underline-offset-4">
                    {servicePageForCategory(c.id).label}
                  </Link>
                )}
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-xs text-slate-500">{disabled ? "Launching soon" : "Starting from"}</span>
                  <span className="font-display font-bold text-xl text-[#0369A1]">{disabled ? "—" : <ServicePrice service={{ ...c, price: c.startingPrice }} />}</span>
                </div>
                {disabled ? (
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-500">Coming soon</div>
                ) : (
                  <Link
                    to={`/booking?category=${c.id}`}
                    data-testid={`category-book-${c.id}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0369A1] hover:text-[#075985]"
                  >
                    Book {c.short} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="bg-white border-y border-slate-200" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Built for everyone</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Serving every kind of customer</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SEGMENTS.map((s) => (
              <div key={s.id} data-testid={`segment-card-${s.id}`} className="bg-[#F8F9FB] rounded-2xl p-7 border border-slate-200">
                <div className="w-12 h-12 rounded-xl bg-[#0369A1] text-white flex items-center justify-center mb-5">
                  <SegIcon id={s.id} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{s.name}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
                <Link
                  to={`/booking?segment=${s.id}`}
                  data-testid={`segment-book-${s.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0369A1] hover:text-[#0369A1]"
                >
                  {s.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="how-it-works">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">How it works</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Book in seconds. Done in hours.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Choose & Book Online", d: "Pick the service, brand and a slot. Takes under 60 seconds." },
            { n: "02", t: "Verified Technician Visits", d: "Aadhar-verified, uniformed technician arrives at your chosen slot." },
            { n: "03", t: "Job Done, Pay Later", d: "Pay only after satisfactory completion — UPI, cards or cash." },
          ].map((s) => (
            <div key={s.n} data-testid={`step-${s.n}`} className="relative bg-white rounded-2xl p-7 border border-slate-200 card-shadow">
              <div className="font-display font-extrabold text-5xl text-[#0369A1]/20">{s.n}</div>
              <h3 className="font-display font-bold text-lg text-slate-900 mt-2">{s.t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE PRICING CARDS */}
      <section className="bg-white border-y border-slate-200" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Transparent Pricing</span>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Most-booked services</h2>
            </div>
            <Link to="/pricing" data-testid="see-all-pricing" className="text-sm font-semibold text-[#0369A1] hover:text-[#0369A1] flex items-center gap-1">
              View full pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {FEATURED_SERVICES.map((s) => {
              const Icon = s.cat.icon;
              return (
                <Link
                  key={s.id}
                  to={`/booking?category=${s.cat.id}&service=${s.id}`}
                  data-testid={`price-card-${s.id}`}
                  className="bg-white rounded-xl p-4 border border-slate-200 hover:border-[#0369A1] hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: s.cat.bg, color: s.cat.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs text-slate-500">{s.cat.short}</span>
                  </div>
                  <h3 className="font-semibold text-sm text-slate-900 leading-snug">{s.name}</h3>
                  <div className="mt-3 font-display font-bold text-lg text-[#0369A1]">
                    <ServicePrice service={s} />
                    {s.id.includes("amc") && <span className="text-xs font-medium text-slate-500">/yr</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRANDS MARQUEE */}
      <section className="py-16" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Brands we service</span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mt-2">Every major brand. Original spares.</h2>
          </div>
        </div>
        <div className="overflow-hidden bg-white border-y border-slate-200 py-8">
          <div className="marquee-track flex gap-12 whitespace-nowrap">
            {ALL_BRANDS_DOUBLED.map((b) => (
              <span key={b.id} className="font-display font-bold text-2xl text-slate-400 hover:text-[#0369A1] transition-colors">
                {b.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" data-testid="trust-badges">
        <div className="bg-[#0F172A] rounded-2xl px-6 py-8 grid grid-cols-2 md:grid-cols-5 gap-6">
          {TRUST_BADGES.map((b) => {
            const Icon = BADGE_ICONS[b.icon] || CheckCircle2;
            return (
              <div key={b.label} className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#0E7490]" />
                </div>
                <span className="text-sm font-semibold leading-tight">{b.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* COVERAGE */}
      <CoverageSection />

      {/* FAQ */}
      <FaqSection />

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="final-cta">
        <div className="bg-gradient-to-r from-[#0369A1] to-[#0369A1] rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="relative">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white">Give your RO the care it deserves.</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">Book your slot in 60 seconds. We'll handle the rest.</p>
            <Link
              to="/booking"
              data-testid="final-cta-book"
              className="inline-flex items-center gap-2 mt-7 bg-white text-[#0369A1] hover:bg-slate-50 font-semibold px-7 py-4 rounded-xl transition-all shadow-lg"
            >
              Book a Service <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
